import { Link } from "react-router-dom";
import { ArrowRight, Check, X, AlertCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useLang } from "../i18n/LanguageContext";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import ServiceIcon from "../components/ServiceIcon";

const PRICED = ["daca", "aos", "remove-conditions", "u-visa", "citizenship", "advance-parole", "vawa", "translation"];

export default function PricingPage() {
  const { t } = useLang();
  return (
    <main data-testid="pricing-page">
      <Seo title={t.seo.pricing.title} desc={t.seo.pricing.desc} />
      <PageHero eyebrow={t.pricing.eyebrow} title={t.pricing.title} sub={t.pricing.sub} />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRICED.map((slug, i) => {
              const s = t.services[slug];
              return (
                <Reveal key={slug} delay={0.05 * i}>
                  <div
                    className={`flex h-full flex-col rounded-2xl border p-8 ${
                      slug === "vawa"
                        ? "border-teal/40 bg-teal/5"
                        : "border-sand/50 bg-cream/60"
                    }`}
                    data-testid={`pricing-card-${slug}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full ${slug === "vawa" ? "bg-teal/15 text-teal" : "bg-terracotta/10 text-terracotta"}`}>
                        <ServiceIcon slug={slug} className="h-5 w-5" />
                      </span>
                      <h2 className="font-serif text-xl font-medium leading-tight text-espresso">{s.name}</h2>
                    </div>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-espresso/70">{s.short}</p>
                    <p className="mt-6" data-testid={`pricing-amount-${slug}`}>
                      <span className="font-serif text-4xl font-semibold text-espresso">{s.price}</span>
                      {s.priceNote && <span className="ml-2 text-sm font-medium text-espresso/60">{s.priceNote}</span>}
                    </p>
                    <Link
                      to={`/contact?service=${slug}`}
                      data-testid={`pricing-cta-${slug}`}
                      className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-espresso px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-terracotta active:scale-95"
                    >
                      {t.common.getStarted}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-10">
            <div className="flex items-start gap-3 rounded-2xl border border-terracotta/40 bg-terracotta/5 p-6" data-testid="gov-fees-notice">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
              <p className="text-sm leading-relaxed text-espresso/85">{t.common.govFeesNote}</p>
            </div>
          </Reveal>
          <Reveal className="mt-4">
            <div className="flex items-start gap-3 rounded-2xl border border-teal/30 bg-teal/5 p-6" data-testid="vawa-scope-notice">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-teal" />
              <p className="text-sm leading-relaxed text-espresso/85">{t.pricing.vawaNote}</p>
            </div>
          </Reveal>

          <div className="mt-20 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-sand/50 bg-white p-8 shadow-sm" data-testid="whats-included-card">
                <h2 className="font-serif text-2xl font-medium text-espresso">{t.pricing.includedTitle}</h2>
                <p className="mt-3 text-xs italic leading-relaxed text-espresso/60">{t.pricing.includedSub}</p>
                <ul className="mt-6 space-y-4">
                  {t.pricing.included.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-espresso/80">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal/15">
                        <Check className="h-3 w-3 text-teal" strokeWidth={3} />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-2xl border border-sand/50 bg-white p-8 shadow-sm" data-testid="not-included-card">
                <h2 className="font-serif text-2xl font-medium text-espresso">{t.pricing.notIncludedTitle}</h2>
                <ul className="mt-6 space-y-4">
                  {t.pricing.notIncluded.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-espresso/80">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-terracotta/15">
                        <X className="h-3 w-3 text-terracotta" strokeWidth={3} />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-20 max-w-3xl">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-espresso" data-testid="pricing-faq-heading">
              {t.pricing.faqTitle}
            </h2>
            <Accordion type="single" collapsible className="mt-4" data-testid="pricing-faq-accordion">
              {t.pricing.faqs.map((f, i) => (
                <AccordionItem key={i} value={`pfaq-${i}`} className="border-sand/50">
                  <AccordionTrigger data-testid={`pricing-faq-trigger-${i}`} className="text-left font-medium text-espresso hover:text-terracotta">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-espresso/75">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal className="mt-16">
            <div className="rounded-2xl bg-espresso p-8 sm:p-10 grain relative overflow-hidden" data-testid="pricing-cta-band">
              <div className="relative flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
                <div>
                  <p className="font-serif text-2xl font-medium text-cream">{t.pricing.ctaTitle}</p>
                  <p className="mt-2 text-sm text-cream/70">{t.pricing.ctaText}</p>
                </div>
                <Link
                  to="/contact"
                  data-testid="pricing-contact-button"
                  className="shrink-0 rounded-full bg-terracotta px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-sand hover:text-espresso active:scale-95"
                >
                  {t.common.contactUs}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
